import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { StoreService } from '@webapp/core/services/store.service';
import { QuotesStoreService } from '@webapp/quotes/store/quotes-store.service';
import { QuotesService } from '@webapp/quotes/services/quotes.service';
import { IActivarStep } from '@webapp/ui';
import { ToastService } from '@webapp/ui/components/toast/toast.service';
import { UsersService } from '@webapp/users/services/users.service';
import { AuthService } from '@webapp/auth/services/auth.service';
import { PoliciesService } from '../../../services/policies.service';
import { QuoterService } from '@webapp/quoter/services/quoter.service';
import { firstValueFrom } from 'rxjs';
import { IUser } from '@webapp/users/interfaces/users.interface';
import { IQuoteWithInsurable, QuoteStatus } from '@webapp/quotes/interfaces/quotes.interface';
import { IssuePolicyInsurableData } from '@webapp/risks/interfaces/issue-policy-insurable.interface';
import { IPayment } from '@webapp/quoter/interfaces/payment.interface';
import { IQuoterPolicy, IQuoterPolicyEmition } from '@webapp/quoter/interfaces/quoter.interface';
import { cleanObject } from '@webapp/core';
import { ICoverage, PAYMENT_GATEWAYS } from '@webapp/payments';
import { CHANNEL, CHANNEL_QUOTE_ENUM } from '@webapp/core/config';
import { RiskDraft, RiskFieldData, RiskIssuanceDraft, RiskPlugin, RiskRegistry } from '@webapp/risk-core';
import { buildIssuePolicySteps } from './issue-policy-steps';



export interface IssuePolicyState {
    quote: IQuoteWithInsurable;
    // Flow-entry snapshot: was the buyer ALREADY a client when this flow
    // started? Captured once in initializeState(); by emission time
    // quote.user.isClient is always true (buildPolicyPayload throws otherwise),
    // so it cannot be re-derived later.
    wasClientAtStart: boolean;
    // Payment data
    payment?: IPayment;
    // UI state
    isLoading: boolean;
    isEmitting: boolean;
    isOptionalCoverageModal: boolean;
    isLeavingModal: boolean;
    // Additional coverage selection
    selectedAdditionalCoverage?: boolean;
    // Coverage chosen by the user (resolved from coverageId query param)
    selectedCoverage?: ICoverage;
    // Selected agent (admin-only)
    agent?: IUser;
    // Step management
    currentStep: number;
    steps: IActivarStep[];
}

@Injectable({ providedIn: 'root' })
export class IssuePolicyStore extends StoreService<IssuePolicyState> implements RiskIssuanceDraft {

    // Injected services
    private readonly quotesStore = inject(QuotesStoreService);
    private readonly quotesService = inject(QuotesService);
    private readonly policiesService = inject(PoliciesService);
    private readonly quoterService = inject(QuoterService);
    private readonly usersService = inject(UsersService);
    private readonly authService = inject(AuthService);
    private readonly toastService = inject(ToastService);
    private readonly router = inject(Router);
    private readonly riskRegistry = inject(RiskRegistry);
    private readonly channel = inject(CHANNEL, { optional: true });


    // Computed selectors from other stores
    readonly quote = this.quotesStore.get();

    // The issuance draft: the single source of truth for the insurable data being
    // collected. A signal so Angular computeds track changes (no stale summary
    // after back/forward navigation in step 2).
    readonly issuePolicyInsurableData = signal<IssuePolicyInsurableData | null>(null);

    // RiskIssuanceDraft port (risk-core): lets risk UI read the draft without importing this store.
    readonly draft = this.issuePolicyInsurableData.asReadonly();

    // Step management selectors
    readonly currentStep = computed(() => this.state()?.currentStep ?? 1);
    readonly steps = computed(() => this.state()?.steps ?? []);
    readonly isEmitting = computed(() => this.state()?.isEmitting ?? false);
    readonly isOptionalCoverageModal = computed(() => this.state()?.isOptionalCoverageModal ?? false);
    readonly isLeavingModal = computed(() => this.state()?.isLeavingModal ?? false);
    readonly selectedAdditionalCoverage = computed(() => this.state()?.selectedAdditionalCoverage ?? false);
    readonly selectedCoverage = computed(() => this.state()?.selectedCoverage);
    // True when THIS flow created the client (prospect converted or unknown
    // status). Drives the onboarding-email copy on the success page.
    readonly isNewClient = computed(() => !this.state()?.wasClientAtStart);
    readonly activeStep = computed(() => {
        const steps = this.steps();
        return steps.find(step => step.active);
    });
    readonly canProceed = computed(() => {
        const currentStep = this.currentStep();
        const steps = this.steps();
        const current = steps.find(s => s.id === currentStep);
        return current && !current.disabled;
    });


    constructor() {
        super();
    }

    initializeState(quote: IQuoteWithInsurable): void {
        // Check if quote is already finished
        if (quote.status === QuoteStatus.Finished) {
            this.toastService.showToast(
                'Cotización ya procesada',
                'Esta cotización ya ha sido procesada y no puede ser utilizada para emitir una nueva póliza.',
                'alert_warning'
            );
            
            // Redirect to quotes list after a short delay
            setTimeout(() => {
                const target = this.channel === CHANNEL_QUOTE_ENUM.DIRECT ? '/dashboard' : '/cotizaciones';
                this.router.navigate([target]);
            }, 2000);
            
            return;
        }
        
        // Pristine capture. Strict === true: legacy/absent flag is treated as
        // NEW (same convention as _shouldSkipUserDataStep, below).
        const wasClientAtStart = quote.user?.isClient === true;

        const plugin = this.riskRegistry.resolve(quote.risk.tag);
        let steps = buildIssuePolicySteps(plugin.issuance.steps);
        let currentStep = 1;

        if (this._shouldSkipUserDataStep(quote.user)) {
            steps = this._enableStep2AndCompleteStep1(steps);
            currentStep = 2;
        }

        const newState: IssuePolicyState = {
            quote,
            wasClientAtStart,
            isLoading: false,
            isEmitting: false,
            isOptionalCoverageModal: false,
            isLeavingModal: false,
            selectedAdditionalCoverage: false,
            currentStep,
            steps,
        };

        this.setState(newState);
        // The draft lives only in this store's signal; plugins hold no per-flow state.
        this.issuePolicyInsurableData.set(null);

        if (quote.additionalCoverage?.id && plugin.capabilities.optionalCoverages) {
            this.showOptionalCoverageModal();
        }
    }

    private _shouldSkipUserDataStep(user: IUser | undefined): boolean {
        if (!user) return false;
        if (this.channel !== CHANNEL_QUOTE_ENUM.DIRECT) return false;
        if (user.isClient !== true) return false;
        return this.usersService.validateUserData(user);
    }

    private _enableStep2AndCompleteStep1(steps: IActivarStep[]): IActivarStep[] {
        return steps.map(step => {
            if (step.id === 1) return { ...step, active: false, completed: true };
            if (step.id === 2) return { ...step, active: true, disabled: false };
            return step;
        });
    }

    setCurrentStep(stepId: number): void {
        const steps = this.steps();
        const targetStep = steps.find(s => s.id === stepId);
        
        if (targetStep && !targetStep.disabled) {
            const updatedSteps = steps.map(step => ({
                ...step,
                active: step.id === stepId
            }));
            
            this.setState({
                ...this.state(),
                currentStep: stepId,
                steps: updatedSteps
            });
        }
    }

    completeCurrentStep(): void {
        const currentStepId = this.currentStep();
        const steps = this.steps();
        
        const updatedSteps = steps.map(step => {
            if (step.id === currentStepId) {
                return { ...step, completed: true };
            }
            // Enable next step
            if (step.id === currentStepId + 1) {
                return { ...step, disabled: false };
            }
            return step;
        });
        
        this.setState({
            ...this.state(),
            steps: updatedSteps
        });
    }

    nextStep(): void {
        const currentStepId = this.currentStep();
        const steps = this.steps();
        const nextStep = steps.find(s => s.id === currentStepId + 1);
        
        if (nextStep) {
            this.completeCurrentStep();
            this.setCurrentStep(nextStep.id);
        }
    }

    async refreshQuote(): Promise<void> {
        const quote = this.quote();
        if (!quote) return;
        await this.quotesStore.detailStore.fetch(quote.id);
    }

    async updateUser(updatedUser: IUser): Promise<boolean> {
        if (!updatedUser.id) {
            this.toastService.showToast(
                'Error',
                'No se pudo identificar el usuario',
                'error'
            );
            return false;
        }

        const originalUser = this.quote()?.user;
        if (!originalUser) {
            this.toastService.showToast(
                'Error',
                'No se encontraron los datos originales del usuario',
                'error'
            );
            return false;
        }

        // Get only the changed fields
        const changes = this.getChangedUserFields(originalUser, updatedUser);

        if (Object.keys(changes).length === 0) {
            return true;
        }

        this.setState({
            ...this.state(),
            isLoading: true
        });

        // Endpoint selection is driven by session identity, not by the edited
        // user's `isClient` business/CRM flag: a self-service session (the
        // authenticated user editing their own profile) must always hit
        // `PUT users/me`, regardless of whether they are already a client or
        // still a prospect. An agent's authenticated id never equals the
        // edited user's id, and an anonymous session is never authenticated,
        // so this fully subsumes the previous `channel === DIRECT` gate.
        const isSelfService =
            this.authService.isAuthenticated() &&
            this.authService.user()?.id === updatedUser.id;

        try {
            // Update user/prospect data
            await firstValueFrom(
                isSelfService
                    ? this.usersService.updateMe(changes)
                    : updatedUser.isClient
                        ? this.usersService.updateUser(updatedUser.id, changes)
                        : this.usersService.updateProspect(updatedUser.id, changes)
            );
            
            this.toastService.showToast(
                'Usuario actualizado correctamente', 
                '',
                'success'
            );
            
            // Check if prospect has complete data and convert to client
            if (!updatedUser.isClient && this.usersService.validateUserData(updatedUser)) {
                try {
                    await firstValueFrom(this.usersService.convertProspectToClient(updatedUser.id));
                    this.toastService.showToast(
                        'Prospecto convertido a cliente',
                        'El prospecto se ha convertido exitosamente a cliente.',
                        'success'
                    );
                    await this.refreshQuote();
                } catch (convertError) {
                    console.error('Error converting prospect to client:', convertError);
                    this.toastService.showToast(
                        'Advertencia',
                        'El usuario se actualizó correctamente pero no se pudo convertir a cliente automáticamente.',
                        'alert_warning'
                    );
                }
            }

            await this.refreshQuote();
            return true;
        } catch {
            this.toastService.showToast(
                'Error',
                'No se pudo actualizar el usuario. Intente nuevamente.',
                'error'
            );
            return false;
        } finally {
            this.setState({
                ...this.state(),
                isLoading: false
            });
        }
    }

    private getChangedUserFields(originalUser: IUser, updatedUser: IUser): Partial<IUser> {
        const changes: Partial<IUser> = {};
        
        // Define which fields are trackable/updatable
        const trackableFields: (keyof IUser)[] = [
            'firstName', 
            'lastName', 
            'companyName', 
            'email',
            'identification', 
            'grossIncomeCondition', 
            'birthDate', 
            'address', 
            'postalCode',
            'zoneId'
        ];

        trackableFields.forEach(field => {
            const originalValue = originalUser[field];
            const updatedValue = updatedUser[field];
            
            // Handle different types of values (strings, nulls, undefined)
            if (this.hasFieldChanged(originalValue, updatedValue)) {
                (changes as any)[field] = updatedValue;
            }
        });

        return changes;
    }

    private hasFieldChanged(original: any, updated: any): boolean {
        // Normalize empty values (null, undefined, empty string) to compare properly
        const normalizeValue = (value: any) => {
            if (value === null || value === undefined || value === '') {
                return null;
            }
            return typeof value === 'string' ? value.trim() : value;
        };

        const normalizedOriginal = normalizeValue(original);
        const normalizedUpdated = normalizeValue(updated);

        return normalizedOriginal !== normalizedUpdated;
    }
    // Issue Policy Insurable Data Management methods
    setIssuePolicyInsurableData(fields: RiskFieldData[]): void {
        const quote = this.quote();
        if (quote?.risk.tag && fields.length > 0) {
            const plugin = this.riskRegistry.resolve(quote.risk.tag);
            this.issuePolicyInsurableData.set(
                plugin.issuance.applyFields(this.currentDraft(), fields) as IssuePolicyInsurableData
            );
        }
    }

    applyFields(fields: RiskFieldData[]): void {
        this.setIssuePolicyInsurableData(fields);
    }

    private currentDraft(): RiskDraft {
        return this.issuePolicyInsurableData() ?? {};
    }

    private pluginInsuredPayload(plugin: RiskPlugin): Record<string, unknown> {
        const { buildInsuredOnlyPayload, buildInsurablePayload } = plugin.issuance;
        return (buildInsuredOnlyPayload ?? buildInsurablePayload)(this.currentDraft());
    }

    getIssuePolicyInsurableData(): IssuePolicyInsurableData | null {
        return this.issuePolicyInsurableData();
    }

    clearIssuePolicyInsurableData(): void {
        this.issuePolicyInsurableData.set(null);
    }

    async saveInsurableData(): Promise<boolean> {
        const quote = this.quote();
        if (!quote?.id || !quote?.risk?.tag) return false;

        this.setState({ ...this.state(), isLoading: true });

        try {
            const payload = this.pluginInsuredPayload(this.riskRegistry.resolve(quote.risk.tag));
            await firstValueFrom(this.quotesService.updateInsurable(quote.id, payload));
            return true;
        } catch (error) {
            console.error('Error saving insurable data:', error);
            this.toastService.showToast(
                'Error',
                'No se pudieron guardar los datos del asegurado. Intente nuevamente.',
                'error'
            );
            return false;
        } finally {
            this.setState({ ...this.state(), isLoading: false });
        }
    }

    async saveClausesData(): Promise<boolean> {
        const quote = this.quote();
        if (!quote?.id || !quote?.risk?.tag) return false;

        const { buildClausesPayload } = this.riskRegistry.resolve(quote.risk.tag).issuance;
        // Clauses are an AP-only concept. Risks without a clauses payload builder
        // have nothing to save — skip the network round-trip and advance the flow.
        if (!buildClausesPayload) {
            return true;
        }
        const clauses = buildClausesPayload(this.currentDraft());

        this.setState({ ...this.state(), isLoading: true });

        try {
            await firstValueFrom(this.quotesService.saveClauses(quote.id, clauses));
            return true;
        } catch (error) {
            console.error('Error saving clauses data:', error);
            this.toastService.showToast(
                'Error',
                'No se pudieron guardar las cláusulas. Intente nuevamente.',
                'error'
            );
            return false;
        } finally {
            this.setState({ ...this.state(), isLoading: false });
        }
    }

    // Payment Management methods
    setPaymentData(payment: IPayment): void {
        this.setState({
            ...this.state(),
            payment
        });
    }

    setAgent(agent: IUser): void {
        this.setState({ ...this.state(), agent });
    }

    getPaymentData(): IPayment | undefined {
        return this.state()?.payment;
    }

    clearPaymentData(): void {
        const currentState = this.state();
        if (currentState) {
            this.setState({
                ...currentState,
                payment: undefined
            });
        }
    }

    // Policy Emission Methods
    async emitPolicy(referralCode?: string | null): Promise<boolean> {
        const quote = this.quote();
        const payment = this.getPaymentData();
        
        if (!quote || !payment) {
            this.toastService.showToast(
                'Error', 
                'Faltan datos requeridos para emitir la póliza', 
                'error'
            );
            return false;
        }

        this.setState({
            ...this.state(),
            isEmitting: true
        });

        try {
            // Build the policy emission payload
            const policyPayload = this.buildPolicyPayload(quote, payment, referralCode);
            
            // Use the policies service emit method
            const policyResponse = await firstValueFrom(
                this.policiesService.emitPolicy(policyPayload, quote.id)
            );
            
            if (policyResponse) {
                this.toastService.showToast(
                    'Éxito', 
                    'Póliza emitida correctamente',
                    'success'
                );
                
                // Complete the flow
                this.completeCurrentStep();
                
                // Optionally store the policy response
                localStorage.setItem('activar_policy', JSON.stringify(policyResponse));
                
                return true;
            } else {
                throw new Error('Error en la emisión de la póliza');
            }
        } catch (error) {
            console.error('Error al emitir la póliza:', error);
            return false;
        } finally {
            this.setState({
                ...this.state(),
                isEmitting: false
            });
        }
    }

    async emitPolicyAccountMoney(referralCode?: string | null): Promise<IQuoterPolicy | null> {
        const quote = this.quote();
        const payment = this.getPaymentData();

        if (!quote || !payment) {
            this.toastService.showToast(
                'Error',
                'Faltan datos requeridos para emitir la póliza',
                'error'
            );
            return null;
        }

        this.setState({ ...this.state(), isEmitting: true });

        try {
            const accountMoneyPayment: IPayment = { type: 'account_money' };
            const policyPayload = this.buildPolicyPayload(quote, accountMoneyPayment, referralCode);

            const policyResponse = await firstValueFrom(
                this.policiesService.emitPolicy(policyPayload, quote.id)
            );

            if (policyResponse) {
                // No success toast: payment is not done yet, the user is redirected to Mercado Pago.
                this.completeCurrentStep();
                localStorage.setItem('activar_policy', JSON.stringify(policyResponse));

                return policyResponse;
            } else {
                throw new Error('Error en la emisión de la póliza');
            }
        } catch (error) {
            console.error('Error al emitir la póliza:', error);
            return null;
        } finally {
            this.setState({ ...this.state(), isEmitting: false });
        }
    }

    async generatePaymentLink(referralCode?: string | null): Promise<boolean> {
        const quote = this.quote();
        const payment = this.getPaymentData();

        if (!quote || !payment) {
            this.toastService.showToast(
                'Error',
                'Faltan datos requeridos para generar el link de pago',
                'error'
            );
            return false;
        }

        this.setState({ ...this.state(), isEmitting: true });

        try {
            const policyPayload = this.buildPolicyPayload(quote, payment, referralCode);
            const response = await firstValueFrom(
                this.quoterService.generatePaymentLinkBeta(policyPayload, quote.id)
            );

            localStorage.setItem('activar_policy_payment_link', JSON.stringify(response));
            return true;
        } catch (error) {
            console.error('Error al generar el link de pago:', error);
            return false;
        } finally {
            this.setState({ ...this.state(), isEmitting: false });
        }
    }

    private buildPolicyPayload(quote: IQuoteWithInsurable, payment: IPayment, referralCode?: string | null): IQuoterPolicyEmition {
        const user = quote.user;
        const selectedCoverage =
            this.selectedCoverage()
            ?? quote.coverages?.find(c => c.select)
            ?? quote.coverages?.[0];

        if (!selectedCoverage) {
            throw new Error('No se encontró cobertura seleccionada');
        }

        // Each plugin applies its own type transformations (e.g. PA strips IUserClause[] → { id: string }[])
        const insurableData = this.riskRegistry.resolve(quote.risk.tag).issuance.buildInsurablePayload(this.currentDraft()) || {};
        
        // Prepare payment with security number if mercado_pago is supported
        const processedPayment = { ...payment };
        
        // Check if risk supports mercado_pago and add securityNumber if needed
        const riskPaymentMethods = quote.risk.paymentMethods || [];
        const supportsMercadoPago = riskPaymentMethods.includes(PAYMENT_GATEWAYS.MERCADO_PAGO);
        
        if (supportsMercadoPago && payment.securityNumber) {
            processedPayment.securityNumber = payment.securityNumber;
        }
        
        // Build policy with insurable data at root level
        const policy: IQuoterPolicyEmition = {
            coverageId: selectedCoverage.id,
            payment: processedPayment,
            ...insurableData // This spreads insurable data (plate, motor, etc.) at root level
        };

        // Guard: emission is always for a client — prospects must be converted first
        if (!user.isClient) {
            throw new Error('Cannot emit policy for a prospect. Convert to client first.');
        }

        // Clean payment data for existing clients
        if (policy.payment.id) {
            delete policy.payment.number;
            delete policy.payment.expiresAt;
            delete policy.payment.name;
            delete policy.payment.type;
            delete policy.payment.exist;
        }
        policy.user = {
            id: user.id
        };

        // Handle additional coverage if selected in this flow
        const selectedAdditionalCoverage = this.selectedAdditionalCoverage();
        if (selectedAdditionalCoverage) {
            policy.additionalCoverage = true;
        } else {
            policy.additionalCoverage = false;
        }

        // Attach referral code when a non-empty value was validated
        if (referralCode && referralCode.trim() !== '') {
            policy.code = referralCode.trim();
        }

        const agentId = this.state().agent?.id;
        if (agentId) {
            policy.agentId = agentId;
        }

        return cleanObject(policy);
    }

    closeOptionalCoverageModal(): void {
        this.setState({
            ...this.state(),
            isOptionalCoverageModal: false
        });
    }

    showOptionalCoverageModal(): void {
        this.setState({
            ...this.state(),
            isOptionalCoverageModal: true
        });
    }

    setSelectedCoverage(coverage: ICoverage): void {
        this.setState({ ...this.state(), selectedCoverage: coverage });
    }

    addOptionalCoverage(): void {
        this.setState({
            ...this.state(),
            selectedAdditionalCoverage: true,
            isOptionalCoverageModal: false
        });
    }

    removeOptionalCoverage(): void {
        this.setState({
            ...this.state(), 
            selectedAdditionalCoverage: false
        });
    }

    getCoverage(): ICoverage | null {
        const quote = this.state()?.quote;
        return quote?.additionalCoverage || null;
    }

    openLeavingModal(): void {
        this.setState({
            ...this.state(),
            isLeavingModal: true
        });
    }

    closeLeavingModal(): void {
        this.setState({
            ...this.state(),
            isLeavingModal: false
        });
    }

    clear(): void {
        this.setState({
            quote: {} as IQuoteWithInsurable,
            wasClientAtStart: false,
            isLoading: false,
            isEmitting: false,
            isOptionalCoverageModal: false,
            isLeavingModal: false,
            selectedAdditionalCoverage: false,
            selectedCoverage: undefined,
            agent: undefined,
            currentStep: 1,
            steps: []
        });
    }

}